import React from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
  useWindowDimensions,
} from "react-native";

import { isHeicImageFile, prepareImageForUpload } from "../../../utils/image";
import { calculateBlobContentFingerprint } from "../../../utils/blobFingerprint";
import { APP_TOKENS as tokens } from "../../../styles/tokens";
import { logUIError } from "../../../services/api-misc";
import { SecondaryButton } from "../native";
import { MULTI_IMAGE_UPLOAD_TOKENS as uploadTokens } from "./MultiImageUpload.tokens";
import "./MultiImageUpload.web.css";

export const VALID_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
export const ACCEPTED_IMAGE_TYPES = [
  ...VALID_IMAGE_TYPES,
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
];
export const IMAGE_INPUT_ACCEPT =
  "image/png,image/jpeg,image/jpg,image/heic,image/heif,.heic,.heif";
export const MAX_FILE_SIZE = 50 * 1024 * 1024;

const imageFingerprintPromises = new WeakMap<Blob, Promise<string>>();

const webPreviewGridStyles = {
  base: {
    display: "flex",
  },
  embedded: {
    display: "grid",
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: tokens.spacing.xs,
    overscrollBehaviorX: "contain",
  },
} satisfies Record<string, React.CSSProperties>;

const webPreviewScrollbarVariables = {
  "--multi-image-scrollbar-size": `${tokens.spacing.sm}px`,
  "--multi-image-scrollbar-track": tokens.color.surfaceMuted,
  "--multi-image-scrollbar-thumb": tokens.color.borderStrong,
  "--multi-image-scrollbar-thumb-hover": tokens.color.textMuted,
  "--multi-image-scrollbar-radius": `${tokens.radius.round}px`,
  "--multi-image-scrollbar-border-width": `${tokens.borderWidth.strong}px`,
} as React.CSSProperties;

const getImageContentFingerprint = (image: Blob): Promise<string> => {
  const cachedFingerprint = imageFingerprintPromises.get(image);
  if (cachedFingerprint) return cachedFingerprint;

  const fingerprintPromise = calculateBlobContentFingerprint(image).catch(
    (error) => {
      imageFingerprintPromises.delete(image);
      throw error;
    },
  );
  imageFingerprintPromises.set(image, fingerprintPromise);
  return fingerprintPromise;
};

type MultiImageUploadProps = {
  images: Blob[];
  onImagesChange: (images: Blob[]) => void;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  maxImages?: number;
  maxFileSize?: number;
  maxImageHeight?: number;
  emptyTitle?: string;
  emptyHint?: string;
  inputAccessibilityLabel?: string;
  testIDPrefix?: string;
  previewsInsideDropZone?: boolean;
  style?: StyleProp<ViewStyle>;
  onError?: (message: string) => void;
  onPreparingChange?: (isPreparing: boolean) => void;
};

type MaterialIconName = "upload" | "delete";

const MaterialIcon: React.FC<{
  name: MaterialIconName;
  color?: string;
  size?: number;
}> = ({
  name,
  color = tokens.color.textPrimary,
  size = tokens.fontSize.xxl,
}) => {
  const paths: Record<MaterialIconName, string> = {
    upload:
      "M19 13v6H5v-6H3v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6h-2ZM13 5.83l2.59 2.58L17 7l-5-5-5 5 1.41 1.41L11 5.83V16h2V5.83Z",
    delete:
      "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12ZM8 9h8v10H8V9Zm7.5-5-1-1h-5l-1 1H5v2h14V4h-3.5Z",
  };

  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path d={paths[name]} fill={color} />
    </svg>
  );
};

const getFileName = (image: Blob, index: number) =>
  image instanceof File && image.name ? image.name : `Photo ${index + 1}`;

const getFileValidationError = (file: File, maxFileSize: number) => {
  const isAcceptedType =
    ACCEPTED_IMAGE_TYPES.includes(file.type.toLowerCase()) ||
    isHeicImageFile(file);

  if (!isAcceptedType) {
    return `${file.name} is not a supported image. Choose a PNG, JPEG, HEIC, or HEIF file.`;
  }

  if (file.size > maxFileSize) {
    const maxMegabytes = Math.round(maxFileSize / (1024 * 1024));
    return `${file.name} is larger than ${maxMegabytes} MB. Choose a smaller image.`;
  }

  return "";
};

const reportImagePreparationFailure = (file: File, error: unknown) => {
  const details =
    error instanceof Error
      ? { message: error.message, stack: error.stack }
      : { message: String(error) };

  void logUIError({
    message: "Image upload preparation failed",
    page_url: typeof window === "undefined" ? null : window.location.href,
    stack_trace: details.stack,
    context: {
      error: details.message,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      isHeic: isHeicImageFile(file),
    },
  }).catch(() => undefined);
};

export const useImagePreviewUrls = (images: readonly Blob[]) => {
  const urlsRef = React.useRef<Map<Blob, string>>(new Map());
  const [previewUrls, setPreviewUrls] = React.useState<string[]>([]);

  React.useEffect(() => {
    const activeImages = new Set(images);

    images.forEach((image) => {
      if (!urlsRef.current.has(image)) {
        urlsRef.current.set(image, URL.createObjectURL(image));
      }
    });

    for (const [image, url] of urlsRef.current.entries()) {
      if (!activeImages.has(image)) {
        URL.revokeObjectURL(url);
        urlsRef.current.delete(image);
      }
    }

    setPreviewUrls(
      images
        .map((image) => urlsRef.current.get(image))
        .filter(Boolean) as string[],
    );
  }, [images]);

  React.useEffect(
    () => () => {
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current.clear();
    },
    [],
  );

  return previewUrls;
};

const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  images,
  onImagesChange,
  label,
  disabled = false,
  loading = false,
  maxImages,
  maxFileSize = MAX_FILE_SIZE,
  maxImageHeight = 1000,
  emptyTitle = "Drop photos here or choose files",
  emptyHint = "Select multiple photos to add them together",
  inputAccessibilityLabel = "Choose photos",
  testIDPrefix = "multi-image-upload",
  previewsInsideDropZone = false,
  style,
  onError,
  onPreparingChange,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < uploadTokens.size.mobileBreakpoint;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const previewScrollRef = React.useRef<HTMLDivElement>(null);
  const dragCounterRef = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isPreparing, setIsPreparing] = React.useState(false);
  const [isDropZoneFocused, setIsDropZoneFocused] = React.useState(false);
  const [isDropZoneHovered, setIsDropZoneHovered] = React.useState(false);
  const [hoveredRemoveIndex, setHoveredRemoveIndex] = React.useState<
    number | null
  >(null);
  const [focusedRemoveIndex, setFocusedRemoveIndex] = React.useState<
    number | null
  >(null);
  const [error, setError] = React.useState("");
  const [previewViewportWidth, setPreviewViewportWidth] = React.useState(0);
  const previewUrls = useImagePreviewUrls(images);
  const isDisabled = disabled || loading || isPreparing;

  React.useEffect(() => {
    if (!previewsInsideDropZone || !images.length) {
      setPreviewViewportWidth(0);
      return;
    }

    const previewScroll = previewScrollRef.current;
    if (!previewScroll) return;

    const updatePreviewViewportWidth = () => {
      const nextWidth = Math.round(previewScroll.clientWidth);
      setPreviewViewportWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth,
      );
    };

    updatePreviewViewportWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updatePreviewViewportWidth);
      return () =>
        window.removeEventListener("resize", updatePreviewViewportWidth);
    }

    const resizeObserver = new ResizeObserver(updatePreviewViewportWidth);
    resizeObserver.observe(previewScroll);
    return () => resizeObserver.disconnect();
  }, [images.length, previewsInsideDropZone]);

  const embeddedPreviewGap = tokens.spacing.md;
  const embeddedCardsPerRow =
    previewViewportWidth > 0
      ? Math.max(
          1,
          Math.floor(
            (previewViewportWidth + embeddedPreviewGap) /
              (uploadTokens.size.embeddedPreviewWidth + embeddedPreviewGap),
          ),
        )
      : images.length;
  const embeddedPreviewRowCount =
    previewsInsideDropZone && images.length > embeddedCardsPerRow ? 2 : 1;
  const embeddedPreviewColumnCount = Math.max(
    1,
    Math.ceil(images.length / embeddedPreviewRowCount),
  );
  const embeddedPreviewContentWidth =
    embeddedPreviewColumnCount * uploadTokens.size.embeddedPreviewWidth +
    Math.max(0, embeddedPreviewColumnCount - 1) * embeddedPreviewGap;
  const embeddedPreviewHasOverflow =
    previewsInsideDropZone &&
    previewViewportWidth > 0 &&
    embeddedPreviewContentWidth > previewViewportWidth;
  const embeddedPreviewGridLayout = previewsInsideDropZone
    ? ({
        gridTemplateColumns: `repeat(${embeddedPreviewColumnCount}, ${uploadTokens.size.embeddedPreviewWidth}px)`,
        gridTemplateRows: `repeat(${embeddedPreviewRowCount}, ${
          uploadTokens.size.embeddedPreviewHeight + tokens.spacing.massive
        }px)`,
      } as React.CSSProperties)
    : undefined;

  const reportError = React.useCallback(
    (message: string) => {
      setError(message);
      onError?.(message);
    },
    [onError],
  );

  const addFiles = React.useCallback(
    async (incomingFiles: File[]) => {
      if (!incomingFiles.length || isDisabled) return;

      setError("");
      const availableSlots = maxImages
        ? Math.max(0, maxImages - images.length)
        : incomingFiles.length;
      if (maxImages && availableSlots === 0) {
        reportError(`You can add up to ${maxImages} photos at once.`);
        return;
      }

      const filesToPrepare = incomingFiles.slice(0, availableSlots);
      let lastError = "";
      if (filesToPrepare.length < incomingFiles.length) {
        lastError = `Only the first ${availableSlots} photos were added. The limit is ${maxImages}.`;
      }

      setIsPreparing(true);
      onPreparingChange?.(true);
      const preparedImages: File[] = [];

      try {
        for (const file of filesToPrepare) {
          const validationError = getFileValidationError(file, maxFileSize);
          if (validationError) {
            lastError = validationError;
            continue;
          }

          try {
            const preparedImage = await prepareImageForUpload(
              file,
              maxImageHeight,
              false,
            );

            const preparedFingerprint =
              await getImageContentFingerprint(preparedImage);
            const possibleDuplicates = [...images, ...preparedImages].filter(
              (image) => image.size === preparedImage.size,
            );
            const existingFingerprints = await Promise.all(
              possibleDuplicates.map(getImageContentFingerprint),
            );
            if (
              existingFingerprints.some(
                (fingerprint) => fingerprint === preparedFingerprint,
              )
            ) {
              lastError = `${file.name} has already been added.`;
              continue;
            }

            preparedImages.push(preparedImage);
          } catch (preparationError) {
            reportImagePreparationFailure(file, preparationError);
            lastError = isHeicImageFile(file)
              ? `${file.name} could not be converted. Export it as a JPEG or PNG, then try again.`
              : `${file.name} could not be prepared. Choose another image and try again.`;
          }
        }

        if (preparedImages.length) {
          onImagesChange([...images, ...preparedImages]);
        }
        if (lastError) reportError(lastError);
      } finally {
        setIsPreparing(false);
        onPreparingChange?.(false);
      }
    },
    [
      images,
      isDisabled,
      maxFileSize,
      maxImageHeight,
      maxImages,
      onImagesChange,
      onPreparingChange,
      reportError,
    ],
  );

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      await addFiles(Array.from(event.target.files || []));
    } finally {
      event.target.value = "";
    }
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDisabled) return;
    dragCounterRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = isDisabled ? "none" : "copy";
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    void addFiles(Array.from(event.dataTransfer.files || []));
  };

  const removeImage = (index: number) => {
    if (isDisabled) return;
    onImagesChange(images.filter((_, imageIndex) => imageIndex !== index));
    setError("");
  };

  const uploadPrompt = (
    <>
      {isPreparing || loading ? (
        <ActivityIndicator size="small" color={tokens.color.accent} />
      ) : (
        <MaterialIcon name="upload" color={tokens.color.accentDark} />
      )}
      <Text style={styles.dropZoneTitle}>
        {isPreparing
          ? "Preparing photos…"
          : loading
            ? "Photos are processing…"
            : isDragging
              ? "Drop photos here"
              : emptyTitle}
      </Text>
      <Text style={styles.dropZoneHint}>{emptyHint}</Text>
    </>
  );

  const selectionContent = images.length ? (
    <View
      testID={`${testIDPrefix}-selection`}
      style={[
        styles.selectionSection,
        previewsInsideDropZone && styles.embeddedSelectionSection,
      ]}
    >
      <View style={styles.selectionHeader}>
        <Text style={styles.selectionCount} accessibilityLiveRegion="polite">
          {images.length} photo{images.length === 1 ? "" : "s"} selected
        </Text>
        <SecondaryButton
          text="Clear All"
          onPress={() => {
            onImagesChange([]);
            setError("");
          }}
          disabled={isDisabled}
          style={styles.clearButton}
        />
      </View>

      <div
        ref={previewScrollRef}
        data-testid={`${testIDPrefix}-preview-scroll`}
        className={
          previewsInsideDropZone ? "multi-image-preview-scroll" : undefined
        }
        role={embeddedPreviewHasOverflow ? "region" : undefined}
        aria-label={
          embeddedPreviewHasOverflow
            ? "Selected photos. Scroll horizontally to see more."
            : undefined
        }
        tabIndex={embeddedPreviewHasOverflow ? 0 : undefined}
        style={{
          ...webPreviewGridStyles.base,
          ...(StyleSheet.flatten(styles.previewGrid) as React.CSSProperties),
          ...(previewsInsideDropZone
            ? webPreviewGridStyles.embedded
            : undefined),
          ...(previewsInsideDropZone
            ? webPreviewScrollbarVariables
            : undefined),
          ...embeddedPreviewGridLayout,
        }}
      >
        {images.map((image, index) => {
          const fileName = getFileName(image, index);
          return (
            <View
              key={`${fileName}-${image.size}-${index}`}
              style={[
                styles.previewCard,
                previewsInsideDropZone && styles.embeddedPreviewCard,
                isMobile && styles.mobileCard,
              ]}
            >
              <Image
                source={{ uri: previewUrls[index] }}
                style={[
                  styles.previewImage,
                  previewsInsideDropZone && styles.embeddedPreviewImage,
                ]}
                resizeMode="contain"
                accessible
                accessibilityLabel={`Selected photo ${index + 1}: ${fileName}`}
              />
              <View style={styles.previewFooter}>
                <Text numberOfLines={1} style={styles.previewName}>
                  {fileName}
                </Text>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Remove ${fileName}`}
                  accessibilityState={{ disabled: isDisabled }}
                  disabled={isDisabled}
                  onPress={() => removeImage(index)}
                  onHoverIn={() => setHoveredRemoveIndex(index)}
                  onHoverOut={() => setHoveredRemoveIndex(null)}
                  onFocus={() => setFocusedRemoveIndex(index)}
                  onBlur={() => setFocusedRemoveIndex(null)}
                  style={({ pressed }) => [
                    styles.iconButton,
                    hoveredRemoveIndex === index && styles.hovered,
                    focusedRemoveIndex === index && styles.iconFocused,
                    pressed && styles.pressed,
                    isDisabled && styles.disabled,
                  ]}
                >
                  <MaterialIcon
                    name="delete"
                    color={tokens.color.textSecondary}
                  />
                </Pressable>
              </View>
            </View>
          );
        })}
      </div>
    </View>
  ) : null;

  return (
    <View
      style={[
        styles.container,
        previewsInsideDropZone && styles.embeddedContainer,
        style,
      ]}
    >
      {label ? <Text style={styles.label}>{label}</Text> : null}
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_INPUT_ACCEPT}
        multiple
        disabled={isDisabled}
        onChange={handleInputChange}
        aria-label={inputAccessibilityLabel}
        data-testid={`${testIDPrefix}-file-input`}
        style={styles.hiddenInput as React.CSSProperties}
      />

      {previewsInsideDropZone ? (
        <div
          data-testid={`${testIDPrefix}-dropzone`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={
            StyleSheet.flatten([
              styles.dropZone,
              styles.webDropZone,
              styles.embeddedDropZone,
              isDragging && styles.dropZoneActive,
              isDropZoneHovered && !isDisabled && styles.hovered,
              isDropZoneFocused && styles.focused,
              isDisabled && styles.disabled,
            ]) as React.CSSProperties
          }
        >
          <Pressable
            testID={`${testIDPrefix}-prompt`}
            accessibilityRole="button"
            accessibilityLabel={inputAccessibilityLabel}
            accessibilityHint="Opens a file picker. You can also drop image files here."
            accessibilityState={{
              disabled: isDisabled,
              busy: isPreparing || loading,
            }}
            {...({
              "aria-busy": isPreparing || loading,
              "aria-disabled": isDisabled,
            } as any)}
            disabled={isDisabled}
            onPress={() => inputRef.current?.click()}
            onFocus={() => setIsDropZoneFocused(true)}
            onBlur={() => setIsDropZoneFocused(false)}
            onHoverIn={() => setIsDropZoneHovered(true)}
            onHoverOut={() => setIsDropZoneHovered(false)}
            style={({ pressed }) => [
              styles.uploadPrompt,
              styles.dropZoneUploadPrompt,
              isDropZoneFocused && styles.focusedPrompt,
              pressed && !isDisabled && styles.pressed,
            ]}
          >
            {uploadPrompt}
          </Pressable>
          {selectionContent}
        </div>
      ) : (
        <>
          <div
            data-testid={`${testIDPrefix}-dropzone`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={
              StyleSheet.flatten([
                styles.dropZone,
                styles.webDropZone,
                isDragging && styles.dropZoneActive,
                isDropZoneHovered && !isDisabled && styles.hovered,
                isDropZoneFocused && styles.focused,
                isDisabled && styles.disabled,
              ]) as React.CSSProperties
            }
          >
            <Pressable
              testID={`${testIDPrefix}-prompt`}
              accessibilityRole="button"
              accessibilityLabel={inputAccessibilityLabel}
              accessibilityHint="Opens a file picker. You can also drop image files here."
              accessibilityState={{
                disabled: isDisabled,
                busy: isPreparing || loading,
              }}
              {...({
                "aria-busy": isPreparing || loading,
                "aria-disabled": isDisabled,
              } as any)}
              disabled={isDisabled}
              onPress={() => inputRef.current?.click()}
              onFocus={() => setIsDropZoneFocused(true)}
              onBlur={() => setIsDropZoneFocused(false)}
              onHoverIn={() => setIsDropZoneHovered(true)}
              onHoverOut={() => setIsDropZoneHovered(false)}
              style={({ pressed }) => [
                styles.uploadPrompt,
                styles.dropZoneUploadPrompt,
                isDropZoneFocused && styles.focusedPrompt,
                pressed && !isDisabled && styles.pressed,
              ]}
            >
              {uploadPrompt}
            </Pressable>
          </div>
          {selectionContent}
        </>
      )}

      {error ? (
        <View style={styles.errorBanner}>
          <Text
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
            style={styles.errorText}
          >
            {error}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexBasis: uploadTokens.size.gridMinWidth,
    minWidth: 0,
    gap: tokens.spacing.md,
  },
  embeddedContainer: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
  },
  label: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
  },
  hiddenInput: {
    display: "none",
  },
  dropZone: {
    minHeight: uploadTokens.size.uploadMinHeight,
    borderWidth: tokens.borderWidth.strong,
    borderStyle: "dashed",
    borderColor: tokens.color.borderStrong,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surfaceMuted,
    alignItems: "center",
    justifyContent: "center",
  },
  dropZoneActive: {
    borderColor: tokens.color.accent,
    backgroundColor: tokens.color.surfaceAccent,
  },
  webDropZone: {
    display: "flex",
    flexDirection: "column",
  },
  embeddedDropZone: {
    alignItems: "stretch",
  },
  uploadPrompt: {
    minHeight: uploadTokens.size.uploadMinHeight,
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing.xxl,
    gap: tokens.spacing.sm,
  },
  dropZoneUploadPrompt: {
    width: "100%",
  },
  dropZoneTitle: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
    textAlign: "center",
  },
  dropZoneHint: {
    color: tokens.color.textMuted,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    textAlign: "center",
  },
  selectionSection: {
    gap: tokens.spacing.md,
  },
  embeddedSelectionSection: {
    width: "100%",
    padding: tokens.spacing.lg,
    borderTopWidth: tokens.borderWidth.thin,
    borderTopColor: tokens.color.borderStrong,
  },
  selectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.md,
  },
  selectionCount: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  clearButton: {
    minHeight: uploadTokens.size.iconButton,
  },
  previewGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: tokens.spacing.md,
  },
  previewCard: {
    flexGrow: 1,
    flexBasis: uploadTokens.size.gridMinWidth,
    maxWidth: "32%",
    height: uploadTokens.size.previewHeight + tokens.spacing.massive,
    overflow: "hidden",
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surface,
  },
  embeddedPreviewCard: {
    flexGrow: 0,
    flexShrink: 0,
    width: uploadTokens.size.embeddedPreviewWidth,
    maxWidth: uploadTokens.size.embeddedPreviewWidth,
    height: uploadTokens.size.embeddedPreviewHeight + tokens.spacing.massive,
  },
  mobileCard: {
    maxWidth: "100%",
    flexBasis: "100%",
  },
  previewImage: {
    width: "100%",
    height: uploadTokens.size.previewHeight,
    backgroundColor: tokens.color.surfaceMuted,
  },
  embeddedPreviewImage: {
    height: uploadTokens.size.embeddedPreviewHeight,
  },
  previewFooter: {
    minHeight: tokens.spacing.massive,
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.sm,
    paddingLeft: tokens.spacing.md,
  },
  previewName: {
    flex: 1,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  iconButton: {
    width: uploadTokens.size.iconButton,
    height: uploadTokens.size.iconButton,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.radius.round,
  },
  errorBanner: {
    padding: tokens.spacing.md,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.errorBorder,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.errorSurface,
  },
  errorText: {
    color: tokens.color.errorText,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  pressed: {
    opacity: tokens.opacity.pressed,
  },
  hovered: {
    backgroundColor: tokens.color.surfaceAccent,
  },
  disabled: {
    opacity: tokens.opacity.disabled,
  },
  focused: {
    borderColor: tokens.color.accentDark,
    borderWidth: tokens.borderWidth.strong,
  },
  focusedPrompt: {
    outlineColor: tokens.color.accentDark,
    outlineOffset: tokens.spacing.xs,
    outlineStyle: "solid",
    outlineWidth: tokens.borderWidth.strong,
  } as any,
  iconFocused: {
    borderWidth: tokens.borderWidth.strong,
    borderColor: tokens.color.accentDark,
  },
});

export default MultiImageUpload;
